import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { Modal, colors, InputText, Button } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { useGroupDashboardPageStore } from 'store/pages/groups-dashboard/pageStore';
import { GeneralRouteV2Id } from 'routes/routesConfigV2';
import { redirectToRoute } from 'routes/redirectToRoute';
import { createStyles } from './styles';
import {
  CREATE_GROUP_MODAL_ACTION_BTN_TEST,
  EXISTING_GROUP_RECORD,
  GROUP_SEARCH_MODAL_TEST,
  SEARCH_GROUP_NAME_INPUT,
} from '../constants';

interface GroupInfoModalProps {
  show: boolean;
  onClose: () => void;
  onAction: () => void;
  withBuildProposal: boolean;
}

const GroupSearchModal: React.FC<GroupInfoModalProps> = observer(({ show, onClose, onAction, withBuildProposal }) => {
  const rootStore = useRootStore();
  const { groupRequestParams } = useGroupDashboardPageStore();
  const {
    searchGroupsModalStore: { groupForm, searchedGroupsList, requestGroupSearchByName, resetSearchedGroupsList },
  } = rootStore;

  const { confirmNewGroupModalDelegate } = useGroupDashboardPageStore();

  function onModalClose(): void {
    groupForm.resetForm();
    resetSearchedGroupsList();
    onClose();
  }

  function onSubmit(e: React.FormEvent): void {
    if (rootStore.domainStoreV2.groupInitiationRedirect) {
      redirectToRoute(GeneralRouteV2Id.INITIATE_GROUP, {});
      return;
    }
    e.preventDefault();
    confirmNewGroupModalDelegate.setGroupName(groupForm.fields.name.value);
    resetSearchedGroupsList();
    onAction();
  }

  function onGroupSearch(e: React.ChangeEvent<HTMLInputElement>): void {
    groupForm.setOnChangeTimeout(e, 250, () => requestGroupSearchByName(groupRequestParams.fullView));
  }

  const classes = createStyles();
  return (
    <Modal show={show} onClose={onModalClose} data-test-id={GROUP_SEARCH_MODAL_TEST}>
      <div className={classes.body}>
        <h3 className={classes.title}>Group Info</h3>
        <p className={classes.subtitle}>Find an existing or create a new group</p>
        <form className={classes.form} onSubmit={onSubmit}>
          <InputText
            label="Group Name"
            name="name"
            value={groupForm.fields.name.value}
            onChange={onGroupSearch}
            placeholder="Search group"
            className={classes.formInput}
            htmlType="search"
            id={SEARCH_GROUP_NAME_INPUT}
          />
          {withBuildProposal && (
            <div className={classes.groupsSearchContainer}>
              <div className={classes.groupProposition}>
                <span className={classes.groupNameProposition}>Create Group: “{groupForm.fields.name.value}...”</span>
                <Button
                  size="medium"
                  htmlType="submit"
                  type="contained"
                  color="custom"
                  customColor={colors.blue[600]}
                  data-test-id={CREATE_GROUP_MODAL_ACTION_BTN_TEST}
                >
                  Create New Group
                </Button>
              </div>
            </div>
          )}

          <ul className={clsx(classes.groupsSearchContainer, classes.groupNamesList)}>
            {searchedGroupsList.map((group) => {
              return (
                <li key={`group_name-${group.name}_group_id-${group.id}`} className={classes.groupProposition}>
                  <div className={classes.groupNameProposition} data-data-test-id={EXISTING_GROUP_RECORD}>
                    <Link className={classes.groupNameText} to={`/group/${group.id}`}>
                      <span>{group.name}</span>
                    </Link>
                  </div>
                  <div className={classes.groupAvailability}>Existing Group</div>
                </li>
              );
            })}
            {searchedGroupsList.length < 1 && <p className={classes.noGroupPlaceholder}>No groups were found...</p>}
          </ul>
        </form>
      </div>
    </Modal>
  );
});

export default GroupSearchModal;
